import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import {CurrentUserProvider} from '@github-ui/agent-sessions/contexts/CurrentUserContext'
import {PullContextProvider} from '@github-ui/agent-sessions/contexts/PullContext'
import {ViewModeProvider} from '@github-ui/agent-sessions/contexts/ViewModeContext'
import {useIssueAgentTasks} from '../contexts/IssueAgentTasksContext'
import {useRepoInfo} from '@github-ui/agent-sessions/hooks/use-repo-info'
import {useTask} from '@github-ui/agent-sessions/hooks/use-task'
import {useTaskPermissions} from '../hooks/use-task-permissions'
import {getTaskTitle} from '@github-ui/agent-sessions/utils/task-helpers'
import {CopilotChatProvider} from '@github-ui/copilot-chat/CopilotChatContext'
import {CurrentRepositoryProvider, type Repository} from '@github-ui/current-repository'
import {RelayEnvironmentProvider, relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
import {SidePanel} from '@github-ui/side-panel'
import {Spinner, Stack} from '@primer/react'
import {Component, lazy, type ReactNode, type RefObject, Suspense, useMemo, useState} from 'react'
import {useChatPayload} from '@github-ui/agent-sessions/components/task-view/hooks/use-chat-payload'
import styles from './AgentTaskSidePanel.module.css'
import {TaskSwitcher} from './TaskSwitcher'

// Lazy-load TaskChat to avoid pulling agent-sessions/TaskChat (and its copilot-immersive-v1
// dependency) into the SSR bundle. The side panel is client-only, so deferring this import
// keeps SSR fast and avoids loading unnecessary code during server rendering.
const TaskChat = lazy(async () => {
  const m = await import('@github-ui/agent-sessions/components/task-view/TaskChat')
  return {default: m.TaskChat}
})

class TaskChatErrorBoundary extends Component<{children: ReactNode}, {error: Error | null}> {
  override state: {error: Error | null} = {error: null}

  static getDerivedStateFromError(error: Error) {
    return {error}
  }

  override render() {
    if (this.state.error) {
      return (
        <div className={styles.emptyState} role="alert">
          Unable to load task chat: {this.state.error.message}
        </div>
      )
    }
    return this.props.children
  }
}

function TaskChatLoadingState() {
  return (
    <div className={styles.loadingContainer}>
      <Spinner size="medium" srText="Loading task chat" />
    </div>
  )
}

const DEFAULT_PANEL_TITLE = 'Agent session'

type AgentTaskSidePanelProps = {
  taskId: string
  repoId: number
  open: boolean
  onClose: () => void
  returnFocusRef: RefObject<HTMLElement | null>
}

export function AgentTaskSidePanel({taskId, repoId, open, onClose, returnFocusRef}: AgentTaskSidePanelProps) {
  const {data: agentTasksData} = useIssueAgentTasks()
  const tasks = useMemo(() => agentTasksData?.tasks ?? [], [agentTasksData?.tasks])
  const assignedAgents = useMemo(() => agentTasksData?.assigned_agents ?? [], [agentTasksData?.assigned_agents])
  const [selectedTaskId, setSelectedTaskId] = useState(taskId)

  const task = tasks.find(t => t.id === selectedTaskId)
  const title = task ? getTaskTitle(task) : DEFAULT_PANEL_TITLE
  const currentRepoId = task?.repository?.id ?? repoId

  return (
    <SidePanel open={open} onClose={onClose} returnFocusRef={returnFocusRef} aria-label={title} width="40%">
      <SidePanel.Heading>
        <Stack direction="horizontal" align="center" padding={'normal'}>
          {task ? (
            <TaskSwitcher
              tasks={tasks}
              selectedTask={task}
              onSelectTask={t => setSelectedTaskId(t.id)}
              assignedAgents={assignedAgents}
            />
          ) : (
            <Spinner size="small" srText="Loading tasks" />
          )}
        </Stack>
      </SidePanel.Heading>
      <div className={styles.headerSeparator} />
      <div className={styles.panel}>
        <ConsecutiveCAPI401sProvider>
          <AgentTaskSidePanelContent taskId={selectedTaskId} repoId={currentRepoId} />
        </ConsecutiveCAPI401sProvider>
      </div>
    </SidePanel>
  )
}

function AgentTaskSidePanelContent({taskId, repoId}: {taskId: string; repoId: number}) {
  const [relayEnvironment] = useState(() => relayEnvironmentWithMissingFieldHandlerForNode())
  const repoIds = useMemo(() => [repoId], [repoId])
  const {data: repoMap, isLoading: isLoadingRepoInfo, isError: isRepoInfoError} = useRepoInfo(repoIds)
  const repoInfo = repoMap[repoId]

  const owner = repoInfo?.ownerLogin ?? ''
  const repo = repoInfo?.name ?? ''

  const {data: taskData, isLoading: isLoadingTask, isError: isTaskError} = useTask({owner, repo, taskId})
  const {data: chatPayload, isLoading: isLoadingChat, isError: isChatError} = useChatPayload()
  const {data: taskPermissions, isLoading: isLoadingPermissions} = useTaskPermissions(owner, repo, taskId)
  const currentUserCanPush = taskPermissions?.currentUserCanPush ?? false
  const steeringEnabled = taskPermissions?.steeringEnabled ?? false
  const pull = taskPermissions?.pull
  const currentUser = taskPermissions?.currentUser

  const sessions = taskData?.sessions ?? []

  const currentUserIsSessionOwner = !!(currentUser && sessions.some(s => s.user_id === currentUser.id))

  const repository = useMemo(
    () =>
      ({
        id: repoId,
        name: repo,
        ownerLogin: owner,
        currentUserCanPush,
      }) as Repository,
    [repoId, repo, owner, currentUserCanPush],
  )

  if (isLoadingTask || isLoadingRepoInfo || isLoadingChat || isLoadingPermissions) {
    return <TaskChatLoadingState />
  }

  if (isTaskError) {
    return (
      <div className={styles.emptyState} role="alert">
        Unable to load task data.
      </div>
    )
  }

  if (isRepoInfoError) {
    return (
      <div className={styles.emptyState} role="alert">
        Unable to load repository information.
      </div>
    )
  }

  if (!repoInfo) {
    return (
      <div className={styles.emptyState} role="status">
        Repository information not available.
      </div>
    )
  }

  if (!taskData || sessions.length === 0) {
    return (
      <div className={styles.emptyState} role="status">
        No session information available.
      </div>
    )
  }

  if (isChatError || !chatPayload) {
    return (
      <div className={styles.emptyState} role="alert">
        Unable to load Copilot Chat. Ensure Copilot is enabled.
      </div>
    )
  }

  return (
    <ViewModeProvider mode="sidePanel">
      <CurrentRepositoryProvider repository={repository}>
        <CurrentUserProvider currentUser={currentUser} currentUserIsSessionOwner={currentUserIsSessionOwner}>
          <PullContextProvider pull={pull}>
            <RelayEnvironmentProvider environment={relayEnvironment}>
              <CopilotChatProvider
                workerPath={chatPayload.findFileWorkerPath}
                threadId={null}
                refs={chatPayload.refs ?? []}
                mode="assistive"
                ssoOrganizations={chatPayload.ssoOrganizations ?? []}
                copilotChatPayload={chatPayload}
              >
                <TaskChatErrorBoundary>
                  <Suspense fallback={<TaskChatLoadingState />}>
                    <TaskChat
                      taskId={taskId}
                      sessions={sessions}
                      currentUser={currentUser}
                      currentUserCanPush={currentUserCanPush}
                      steeringEnabled={steeringEnabled}
                      logsPollingInterval={3000}
                      repoOwner={owner}
                      repoName={repo}
                      task={taskData}
                    />
                  </Suspense>
                </TaskChatErrorBoundary>
              </CopilotChatProvider>
            </RelayEnvironmentProvider>
          </PullContextProvider>
        </CurrentUserProvider>
      </CurrentRepositoryProvider>
    </ViewModeProvider>
  )
}
